/**
 * Error Boundary Component
 * Catches errors in child components and displays fallback UI
 * Only the failing component is affected, not the entire app
 */

import React from 'react';
import logger from '../Utils/logger';
import styles from './ErrorBoundary.module.css';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const errorCount = this.state.errorCount + 1;

    this.setState({
      error,
      errorInfo,
      errorCount,
    });

    // Log error for debugging
    logger.error(
      `Error in ${this.props.componentName || 'Component'}`,
      error
    );

    // If too many errors, disable auto-recovery
    if (errorCount > 3) {
      logger.error(`Component failed ${errorCount} times, disabling auto-recovery`);
    }
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      const isDevelopment = process.env.NODE_ENV === 'development';

      return (
        <div className={styles.errorContainer}>
          <div className={styles.errorCard}>
            <div className={styles.iconWrapper}>⚠️</div>
            <h2 className={styles.title}>Something went wrong</h2>

            <p className={styles.message}>
              {this.props.fallbackMessage || 'This component encountered an error. Please try again.'}
            </p>

            {isDevelopment && this.state.error && (
              <details className={styles.details}>
                <summary>
                  <span>Error Details (Development Only)</span>
                  <span>▼</span>
                </summary>
                <pre className={styles.errorStack}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}

            <div className={styles.buttonGroup}>
              <button
                onClick={this.resetError}
                className={styles.retryButton}
                disabled={this.state.errorCount > 3}
              >
                {this.state.errorCount > 3
                  ? 'Error persists - Reload page'
                  : 'Try Again'}
              </button>

              {this.state.errorCount > 3 && (
                <button
                  onClick={() => window.location.reload()}
                  className={styles.reloadButton}
                >
                  Reload Page
                </button>
              )}
            </div>

            {this.state.errorCount > 0 && (
              <div className={styles.errorCounter}>
                Recovery attempts: {this.state.errorCount}/4
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
